// Pantallas de la verificación en dos pasos (TOTP) del HCE — MT-043-C. Las usan tres lugares con los MISMOS pasos y por eso están aquí, no repetidas:
// el acceso (LandingAcceso: personal clínico con ticket y administrador con token de enrolamiento), la página «Seguridad de la cuenta» del personal
// clínico y el panel de Usuarios del administrador. Ninguna guarda nada: el secreto, el QR y los códigos de respaldo viven solo en el estado de
// la pantalla y desaparecen al continuar.
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { descargarBlob, pdfDeBase64, type CodigosRespaldo, type DatosEnrolamiento, type ErrorMfa } from '@/lib/hceMfa';

type Resultado<T> = ({ ok: true } & T) | ErrorMfa;

const ETIQUETA = 'block text-xs font-mono uppercase tracking-wide text-hce-muted mb-1';

/** Segundo paso del acceso: el código de 6 dígitos de la app autenticadora, o uno de respaldo. `onEnviar` devuelve el mensaje de error o `null` si salió bien. */
export function PasoCodigo({ onEnviar, onCancelar }: { onEnviar: (codigo: string) => Promise<string | null>; onCancelar: () => void }): JSX.Element {
  const { t } = useTranslation();
  const [codigo, setCodigo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formulario = useRef<HTMLFormElement>(null);

  // Al abrir el paso (y tras un error) el cursor va al campo del código: quien lo escribe no tiene que buscarlo.
  useEffect(() => {
    formulario.current?.querySelector('input')?.focus();
  }, [error]);

  async function enviar(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!codigo.trim()) return;
    setError(null);
    setEnviando(true);
    const msg = await onEnviar(codigo.trim());
    setEnviando(false);
    if (msg) {
      setError(msg);
      setCodigo('');
    }
  }

  return (
    <form ref={formulario} onSubmit={(e) => void enviar(e)} className="space-y-3" aria-label={t('auth.mfa.codigo.titulo')}>
      <p className="text-sm font-medium text-hce-ink">{t('auth.mfa.codigo.titulo')}</p>
      <p className="text-xs text-hce-muted">{t('auth.mfa.codigo.ayuda')}</p>
      {error && (
        <Alert variant="red">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div>
        <label htmlFor="mfa-codigo" className={ETIQUETA}>
          {t('auth.mfa.codigo.etiqueta')}
        </label>
        <Input
          id="mfa-codigo"
          value={codigo}
          onChange={(e) => setCodigo(e.target.value)}
          autoComplete="one-time-code"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          maxLength={64}
          required
        />
      </div>
      <p className="text-xs text-hce-muted">{t('auth.mfa.codigo.respaldo')}</p>
      <Button type="submit" disabled={enviando || !codigo.trim()} className="w-full">
        {enviando ? t('auth.mfa.codigo.verificando') : t('auth.mfa.codigo.verificar')}
      </Button>
      <Button type="button" variant="outline" onClick={onCancelar} disabled={enviando} className="w-full">
        {t('auth.mfa.volver')}
      </Button>
    </form>
  );
}

/** Los 10 códigos de respaldo (única vez que se ven) con su PDF; el botón de continuar exige confirmar que se guardaron. */
export function VistaCodigosRespaldo({ codigos, onContinuar, textoContinuar }: { codigos: CodigosRespaldo; onContinuar: () => void; textoContinuar?: string }): JSX.Element {
  const { t } = useTranslation();
  const [guardados, setGuardados] = useState(false);
  const [copiado, setCopiado] = useState(false);

  function descargarPdf(): void {
    descargarBlob(pdfDeBase64(codigos.backupCodesPdf), 'codigos-de-respaldo-hce.pdf');
  }
  async function copiar(): Promise<void> {
    try {
      await navigator.clipboard.writeText(codigos.backupCodes.join('\n'));
      setCopiado(true);
    } catch {
      setCopiado(false);
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-hce-ink">{t('auth.mfa.respaldo.titulo')}</p>
      <Alert>
        <AlertDescription>{t('auth.mfa.respaldo.aviso')}</AlertDescription>
      </Alert>
      <ul className="grid grid-cols-2 gap-1 rounded-md border border-border bg-hce-paper p-3 font-mono text-sm text-hce-ink" aria-label={t('auth.mfa.respaldo.titulo')}>
        {codigos.backupCodes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={descargarPdf}>
          {t('auth.mfa.respaldo.descargarPdf')}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => void copiar()}>
          {copiado ? t('auth.mfa.respaldo.copiado') : t('auth.mfa.respaldo.copiar')}
        </Button>
      </div>
      <div className="flex items-start gap-2 text-xs text-hce-ink">
        <input id="mfa-guardados" type="checkbox" aria-label={t('auth.mfa.respaldo.guardados')} checked={guardados} onChange={(e) => setGuardados(e.target.checked)} className="mt-0.5" />
        <label htmlFor="mfa-guardados">{t('auth.mfa.respaldo.guardados')}</label>
      </div>
      <Button type="button" disabled={!guardados} onClick={onContinuar} className="w-full">
        {textoContinuar ?? t('auth.mfa.continuar')}
      </Button>
    </div>
  );
}

/** El secreto en grupos de 4 para teclearlo a mano si no se puede escanear el QR. */
export function secretoEnGrupos(secreto: string): string {
  return secreto.replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim();
}

/**
 * Enrolamiento: QR -> primer código -> códigos de respaldo. `iniciar` pide el secreto (se llama UNA vez al abrir la pantalla); `confirmar` activa el
 * factor con el primer código. Al terminar de ver los códigos llama a `onTerminar` con lo que devolvió `confirmar`, para que quien lo montó siga
 * (abrir la sesión, volver a la lista, etc.).
 */
export function PasoEnrolamiento<T extends CodigosRespaldo>({
  iniciar,
  confirmar,
  onTerminar,
  onCancelar,
  textoContinuar,
}: {
  iniciar: () => Promise<Resultado<DatosEnrolamiento>>;
  confirmar: (codigo: string) => Promise<Resultado<T>>;
  onTerminar: (resultado: T) => void;
  onCancelar?: () => void;
  textoContinuar?: string;
}): JSX.Element {
  const { t } = useTranslation();
  const [datos, setDatos] = useState<DatosEnrolamiento | null>(null);
  const [errorInicio, setErrorInicio] = useState<string | null>(null);
  const [codigo, setCodigo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [terminado, setTerminado] = useState<T | null>(null);
  const pedido = useRef(false);

  useEffect(() => {
    // Una sola vez: pedir el secreto dos veces (StrictMode) dejaría en pantalla un QR que ya no corresponde al guardado.
    if (pedido.current) return;
    pedido.current = true;
    void iniciar().then((r) => {
      if (r.ok) setDatos({ secreto: r.secreto, uri: r.uri, qr: r.qr });
      else setErrorInicio(r.error);
    });
  }, [iniciar]);

  async function enviar(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setEnviando(true);
    const r = await confirmar(codigo.trim());
    setEnviando(false);
    if (r.ok) setTerminado(r);
    else {
      setError(r.error);
      setCodigo('');
    }
  }

  if (terminado) return <VistaCodigosRespaldo codigos={terminado} onContinuar={() => onTerminar(terminado)} textoContinuar={textoContinuar} />;

  if (errorInicio) {
    return (
      <div className="space-y-3">
        <Alert variant="red">
          <AlertDescription>{errorInicio}</AlertDescription>
        </Alert>
        {onCancelar && (
          <Button type="button" variant="outline" onClick={onCancelar} className="w-full">
            {t('auth.mfa.volver')}
          </Button>
        )}
      </div>
    );
  }
  if (!datos) return <p className="text-sm text-hce-muted">{t('auth.mfa.enrolar.preparando')}</p>;

  return (
    <form onSubmit={(e) => void enviar(e)} className="space-y-3" aria-label={t('auth.mfa.enrolar.titulo')}>
      <p className="text-sm font-medium text-hce-ink">{t('auth.mfa.enrolar.titulo')}</p>
      <ol className="list-decimal space-y-1 pl-4 text-xs text-hce-muted">
        <li>{t('auth.mfa.enrolar.paso1')}</li>
        <li>{t('auth.mfa.enrolar.paso2')}</li>
        <li>{t('auth.mfa.enrolar.paso3')}</li>
      </ol>
      <div className="flex justify-center">
        <img src={datos.qr} alt={t('auth.mfa.enrolar.qrAlt')} width={220} height={220} className="rounded-md border border-border bg-white" />
      </div>
      <details className="text-xs text-hce-muted">
        <summary className="cursor-pointer">{t('auth.mfa.enrolar.noPuedoEscanear')}</summary>
        <p className="mt-1">{t('auth.mfa.enrolar.claveManual')}</p>
        <p className="mt-1 select-all break-all font-mono text-sm text-hce-ink" data-testid="mfa-secreto">
          {secretoEnGrupos(datos.secreto)}
        </p>
      </details>
      {error && (
        <Alert variant="red">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div>
        <label htmlFor="mfa-primer-codigo" className={ETIQUETA}>
          {t('auth.mfa.enrolar.primerCodigo')}
        </label>
        <Input id="mfa-primer-codigo" value={codigo} onChange={(e) => setCodigo(e.target.value)} inputMode="numeric" autoComplete="one-time-code" maxLength={10} required />
      </div>
      <Button type="submit" disabled={enviando || !codigo.trim()} className="w-full">
        {enviando ? t('auth.mfa.enrolar.activando') : t('auth.mfa.enrolar.activar')}
      </Button>
      {onCancelar && (
        <Button type="button" variant="outline" onClick={onCancelar} disabled={enviando} className="w-full">
          {t('auth.mfa.volver')}
        </Button>
      )}
    </form>
  );
}
